/**
 * Labstep
 *
 * @module components/Comment/List
 * @desc List of comments
 */

import cx from 'classnames';
import { CommentItemContainer } from 'labstep-web/components/Comment/Item';
import { CommentListPin } from 'labstep-web/components/Comment/List/Pin';
import { EntitySearchContainer as EntitySearchCursor } from 'labstep-web/components/Entity/Search/Cursor';
import { Filter } from 'labstep-web/constants/filters';
import { Comment } from 'labstep-web/models/comment.model';
import type React from 'react';
import styles from './styles.module.scss';
import type {
  ICommentListProps,
  ICommentListReadContainerProps,
} from './types';

export const CommentList: React.FC<ICommentListProps> = ({
  comments,
  commentItemProps,
  showPin,
  header,
}) => (
  <div className={styles.container}>
    <div className={styles.content}>
      {comments.map((comment) => (
        <div key={`${comment.id}`} className={styles.listItem}>
          <CommentItemContainer
            comment={comment}
            header={header}
            showPin={showPin}
            {...commentItemProps}
          />
        </div>
      ))}
    </div>
  </div>
);

export const CommentListReadContainer: React.FC<
  ICommentListReadContainerProps
> = ({
  threadId,
  experimentWorkflow,
  reverse,
  extraReadParams,
  noCommentsMessage,
  ...rest
}) => {
  const params = experimentWorkflow
    ? {
        experiment_workflow_id: experimentWorkflow.id,
        ...extraReadParams,
      }
    : {
        parent_thread_id: threadId,
        ...extraReadParams,
      };
  return (
    <>
      <CommentListPin
        experimentWorkflowId={
          experimentWorkflow ? experimentWorkflow.id : undefined
        }
        threadId={threadId}
        header={rest.header}
      />

      <EntitySearchCursor
        key={JSON.stringify(params)}
        entityName={Comment.entityName}
        reverse={reverse}
        params={params}
        noResultsMessage={noCommentsMessage as React.ReactNode}
        filters={[
          Filter.deleted,
          Filter.date_range,
          Filter.created_by,
        ]}
      >
        {({ entities }) => (
          <div
            data-cy="components/Comment/List:container"
            className={cx('commentList')}
            data-testid="comment-list"
          >
            <CommentList comments={entities as Comment[]} {...rest} />
          </div>
        )}
      </EntitySearchCursor>
    </>
  );
};

CommentListReadContainer.defaultProps = {
  reverse: true,
  extraReadParams: { count: 10 },
};
